import {StoreUtils} from './store-utils';
import {StoreSettingUtil} from './store-setting-util';
import {SharedPreference} from '../shared-preference/shared-perference';
import { CurrencyService } from '../services/currency.service';

export class CurrencyUtil {
  static getExchangeRate(): number {
    const displayCurrency = CurrencyService.getDisplayCurrency();
    if (displayCurrency === 'USD' || displayCurrency === '') {
      return 1;
    }
    const exchange = StoreSettingUtil.getCurrencyExchange();
    return exchange ? exchange.rate : 4000; // Default fallback if not found
  }

  static display(amount: number | null, allowNegative: boolean = false): string {
    try {
      if (amount === null) amount = 0;
      if (!allowNegative && amount <= 0) amount = 0;

      if (CurrencyService.isDisplayCurrencyKHR()) {
        return this.formatKHR(amount);
      } else if (CurrencyService.isDisplayCurrencyUSD()) {
        return this.formatUSD(amount);
      } else {
        return this.formatByCurrency(amount, this.getCurrencySymbol()).replace('.00', '');
      }
    }catch (e) {
      return amount.toString();
    }
  }

  private static formatUSD(amount: number): string {
    // Check if Intl is available, else fallback to manual formatting
    if (typeof Intl !== 'undefined' && Intl.NumberFormat) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
      }).format(amount);
    }
    return '$ ' + amount.toFixed(2);
  }

  private static formatKHR(amount: number): string {
    if (typeof Intl !== 'undefined' && Intl.NumberFormat) {
      // KHR is used without decimals, so never show a fractional riel ("៛ 3,966", not "៛ 3,965.53")
      return new Intl.NumberFormat('en-KH', {
        style: 'currency',
        currency: 'KHR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount).replace('KHR', '៛').trim();
    }
    return amount.toFixed(0) + ' ៛';
  }

  private static formatByCurrency(amount: number, symbol: string): string {
    return symbol + ' ' + amount.toFixed(2);
  }

  static replaceCurrency(value: string): string {
    if (['$'].indexOf(this.getCurrencySymbol()) !== -1) {
      return value;
    }
    return value.replace('.00', '');
  }

  static getDisplayCurrencySymbol(): string {
    if (CurrencyService.isDisplayCurrencyUSD()) {
      return "$";
    } else if (CurrencyService.isDisplayCurrencyKHR()) {
      return "៛";
    } else {
      return this.getCurrencySymbol();
    }
  }

  static getCurrencySymbol(): string {
    const user = SharedPreference.getUser();
    let storeId = user ? user.currentStoreId : null;

    if (!storeId) {
      storeId = SharedPreference.getStoreId();
    }

    if (this.isShowPairCurrency()) {
      const exchange = StoreSettingUtil.getCurrencyExchange();
      if (exchange && exchange.to === "KHR") {
        return "៛";
      } else {
        return exchange ? exchange.symbol : "$";
      }
    }
    return StoreUtils.getCurrencySymbol(storeId) || "$";
  }

  static reverseString(s: string): string {
    return s.split("").reverse().join("");
  }

  static insertString(str: string, indexToInsert: number, valueToInsert: string): string {
    try {
      const reversed = this.reverseString(str);
      const modified = reversed.substring(0, indexToInsert) + valueToInsert + reversed.substring(indexToInsert);
      return this.reverseString(modified);
    } catch (err) {
      return str;
    }
  }

  static restoreConversionValue(convertedValue: number, length: number): number {
    try {
      return this.round(convertedValue, length);
    } catch (e) {
      return convertedValue;
    }
  }

  private static getConversionRateLength(conversionRatePairUSD: number): number {
    return Math.floor(conversionRatePairUSD).toString().length - 2;
  }

  static roundConversionValue(valueToRound: number | null, conversionRate?: number): number {
    if (valueToRound === null) return 0;
    const rate = conversionRate !== undefined ? conversionRate : this.getExchangeRate();
    const conversionRatePairUSDLength = this.getConversionRateLength(rate);
    try {
      if (conversionRatePairUSDLength < 2) return this.round(valueToRound, 2);
    } catch (e) {}
    const conversionRoundDigitLength = -(conversionRatePairUSDLength - 2);
    return this.restoreConversionValue(valueToRound, conversionRoundDigitLength);
  }

  static round(num: number, places: number): number {
    const factor = Math.pow(10, places);
    return Math.round(num * factor) / factor;
  }

  static convertToUSD(inputValue: number, conversionRate?: number): number {
    const decimalFraction = 6;
    const rate = conversionRate !== undefined ? conversionRate : this.getExchangeRate();
    const convertedValue = inputValue / rate;
    return this.round(convertedValue, decimalFraction);
  }

  static convertFromUSD(valueToConvert: number | null, conversionRate?: number, roundValue: boolean = true): number {
    const rate = conversionRate !== undefined ? conversionRate : this.getExchangeRate();
    let revertedValue = ((valueToConvert || 0) * rate) - 0.00000000000005;
    if (roundValue) {
      revertedValue = this.roundConversionValue(revertedValue, rate);
    }
    return revertedValue;
  }

  static getValue(value: number | null, conversionRate?: number): number {
    const rate = conversionRate !== undefined ? conversionRate : this.getExchangeRate();
    if (value === null) return 0.0;
    if (!CurrencyService.isDisplayCurrencyUSD()) {
      value = this.convertFromUSD(value, rate);
    }
    return value;
  }

  static setValue(value: number | null, conversionRate?: number, privateValue?: number): number | null {
    if (value !== null && !CurrencyService.isDisplayCurrencyUSD()) {
      const rate = conversionRate !== undefined ? conversionRate : this.getExchangeRate();
      value = this.convertToUSD(value, rate);
      if (privateValue !== undefined && privateValue !== null) {
        const diff = privateValue - value;
        if (diff >= -0.0001 && diff <= 0.0001) {
          value = privateValue;
        }
      }
    }
    return value;
  }

  static isShowPairCurrency(): boolean {
    const user = SharedPreference.getUser();
    const storeId = user ? user.currentStoreId : null;
    const exchange = StoreSettingUtil.getCurrencyExchange();
    return StoreSettingUtil.isSecondaryCurrencyHasData(storeId) &&
           StoreUtils.getCurrencyDual(storeId) === true &&
           !!exchange && exchange.to !== "USD";
  }
}
